'use client';

import { Check, Sparkles } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { EDITOR_EFFECTS, normalizeEffectId } from '@/lib/editor/effects';
import { useEditorStore } from '@/lib/editor/editorStore';
import { EditorEffectVisual } from '../EditorEffectVisual';

/**
 * Full-frame effect browser. The miniature uses the same visual component as
 * the player overlay, so clicking a card never reveals a different effect.
 */
export function EffectsPanel() {
  const { t } = useI18n();
  const addEffectLayer = useEditorStore((s) => s.addEffectLayer);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const selectedEffect = useEditorStore((s) => {
    const layer = s.layers.find((item) => item.id === selectedLayerId);
    return layer?.type === 'effect' ? normalizeEffectId(layer.src, layer.text) : null;
  });

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.toolEffects')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="flex items-center gap-2 text-xs font-bold text-white/80">
            <Sparkles className="h-3.5 w-3.5 text-brand-300" />
            Эффекты поверх видео
          </div>
          <p className="mt-1 text-[11px] leading-5 text-white/45">{t('studio.editor.effectDescription')}</p>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          {EDITOR_EFFECTS.map((effect) => {
            const active = selectedEffect === effect.id;
            return (
              <button
                key={effect.id}
                type="button"
                onClick={() => addEffectLayer(effect.id, effect.name)}
                className={`group overflow-hidden rounded-xl border text-left transition ${
                  active
                    ? 'border-brand-400 bg-brand-600/20 ring-2 ring-brand-400/60'
                    : 'border-white/10 bg-white/[0.04] hover:border-brand-500/80 hover:bg-white/[0.07]'
                }`}
              >
                <span className="relative block aspect-video overflow-hidden bg-[linear-gradient(145deg,#372b55,#101826_48%,#26445b)]">
                  <span className="absolute inset-0 bg-[radial-gradient(circle_at_66%_35%,#f6d5a9_0_8%,transparent_9%),linear-gradient(25deg,transparent_0_42%,#172033_43%_58%,transparent_59%)] opacity-75" />
                  <EditorEffectVisual effect={effect.id} color={effect.defaultColor} compact />
                  {active && (
                    <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </span>
                <span className="block px-2 py-2">
                  <span className="block text-[11px] font-black leading-4 text-white/85">{effect.name}</span>
                  <span className="mt-0.5 block truncate text-[9px] text-white/35">{effect.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
